<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { Modal, message } from "ant-design-vue";
import {
  deleteAdminPetAction,
  listAdminPetActions,
  saveAdminPetAction,
  type AdminPetAction
} from "../admin-api";

const loading = ref(false);
const saving = ref(false);
const removingKey = ref("");
const actions = ref<AdminPetAction[]>([]);
const detailAction = ref<AdminPetAction | null>(null);
const editorMode = ref<"create" | "edit" | null>(null);
const form = reactive<AdminPetAction>(blankAction());

const columns = [
  { title: "动作", key: "name" },
  { title: "Key", key: "key", width: 140 },
  { title: "播放", key: "play", width: 160 },
  { title: "成本", key: "creditCost", width: 90 },
  { title: "状态", key: "enabled", width: 90 },
  { title: "排序", key: "sortOrder", dataIndex: "sortOrder", width: 90 },
  { title: "操作", key: "actions", width: 220 }
];

const enabledCount = computed(() => actions.value.filter((action) => action.enabled).length);
const defaultCount = computed(() => actions.value.filter((action) => action.isDefault).length);

onMounted(loadActions);

function blankAction(): AdminPetAction {
  return {
    key: "",
    name: "",
    nameZh: "",
    nameEn: "",
    promptSnippet: "",
    isDefault: true,
    creditCost: 1,
    sortOrder: 0,
    enabled: true,
    mirrored: false,
    fps: 12,
    loop: true,
    message: "",
    messageZh: "",
    messageEn: ""
  };
}

async function loadActions() {
  loading.value = true;
  try {
    actions.value = await listAdminPetActions();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "动作列表加载失败。");
  } finally {
    loading.value = false;
  }
}

function openCreate() {
  editorMode.value = "create";
  Object.assign(form, blankAction(), { sortOrder: actions.value.length * 10 + 10 });
}

function openEdit(action: AdminPetAction) {
  editorMode.value = "edit";
  Object.assign(form, {
    ...action,
    nameZh: action.nameZh || action.name,
    nameEn: action.nameEn || action.name,
    messageZh: action.messageZh || action.message,
    messageEn: action.messageEn || action.message
  });
}

function closeEditor() {
  editorMode.value = null;
}

async function submitAction() {
  saving.value = true;
  try {
    const response = await saveAdminPetAction({
      ...form,
      name: form.nameZh || form.name,
      message: normalizeMessages(form.messageZh || form.message),
      messageZh: normalizeMessages(form.messageZh || form.message),
      messageEn: normalizeMessages(form.messageEn),
      loop: shouldLoop(form)
    });
    message.success(response.message);
    closeEditor();
    await loadActions();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "动作保存失败。");
  } finally {
    saving.value = false;
  }
}

function confirmRemove(action: AdminPetAction) {
  Modal.confirm({
    title: "删除动作",
    content: `确定删除动作「${displayName(action)}」(${action.key})？`,
    okText: "删除",
    okType: "danger",
    cancelText: "取消",
    async onOk() {
      await remove(action);
    }
  });
}

async function remove(action: AdminPetAction) {
  removingKey.value = action.key;
  try {
    await deleteAdminPetAction(action.key);
    message.success("动作已删除。");
    if (detailAction.value?.key === action.key) {
      detailAction.value = null;
    }
    await loadActions();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "删除失败。");
  } finally {
    removingKey.value = "";
  }
}

function actionMessages(action: AdminPetAction | null, locale: "zh" | "en" = "zh"): string[] {
  const value = locale === "en" ? action?.messageEn : action?.messageZh;
  return (value || action?.message || "")
    .split(/\r?\n|\r/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function messageSummary(action: AdminPetAction): string {
  const messages = actionMessages(action, "zh");
  if (!messages.length) {
    return "未设置桌宠对白";
  }

  return messages.length === 1 ? messages[0] : `${messages.length} 条对白 · ${messages[0]}`;
}

function displayName(action: AdminPetAction): string {
  return action.nameZh || action.name || action.key;
}

function normalizeMessages(value: string): string {
  return value
    .split(/\r?\n|\r/g)
    .map((item) => item.trim())
    .filter(Boolean)
    .join("\n");
}

function shouldLoop(action: AdminPetAction): boolean {
  return action.key === "idle" || action.key.startsWith("walk") || action.mirrored;
}

</script>

<template>
  <a-space class="admin-page-stack" direction="vertical" size="large">
    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :md="12"><a-card><a-statistic title="动作总数" :value="actions.length" /></a-card></a-col>
      <a-col :xs="24" :md="12"><a-card><a-statistic title="默认生成" :value="defaultCount" /></a-card></a-col>
    </a-row>

    <a-card>
      <template #title>动作配置</template>
      <template #extra><a-button type="primary" @click="openCreate">新增动作</a-button></template>

      <a-table
        row-key="key"
        :columns="columns"
        :data-source="actions"
        :loading="loading"
        :pagination="{ pageSize: 10, showSizeChanger: true }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <strong>{{ displayName(record) }}</strong>
            <div v-if="record.nameEn" class="admin-table-subtext">EN: {{ record.nameEn }}</div>
            <div class="admin-table-subtext">{{ messageSummary(record) }}</div>
          </template>
          <template v-else-if="column.key === 'key'"><a-typography-text code>{{ record.key }}</a-typography-text></template>
          <template v-else-if="column.key === 'play'">
            {{ record.fps }} fps · {{ record.mirrored ? "左右镜像" : "单向" }}
          </template>
          <template v-else-if="column.key === 'enabled'">
            <a-tag :color="record.enabled ? 'green' : 'default'">{{ record.enabled ? "启用" : "停用" }}</a-tag>
          </template>
          <template v-else-if="column.key === 'actions'">
            <a-space wrap>
              <a-button size="small" @click="detailAction = record">查看</a-button>
              <a-button size="small" type="primary" ghost @click="openEdit(record)">编辑</a-button>
              <a-button size="small" danger :loading="removingKey === record.key" @click="confirmRemove(record)">删除</a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      :title="editorMode === 'create' ? '新增动作' : '编辑动作'"
      :open="Boolean(editorMode)"
      :confirm-loading="saving"
      width="780px"
      ok-text="保存"
      cancel-text="取消"
      @ok="submitAction"
      @cancel="closeEditor"
    >
      <a-form layout="vertical" :model="form">
        <a-row :gutter="16">
          <a-col :span="12"><a-form-item label="动作 Key"><a-input v-model:value="form.key" :disabled="editorMode === 'edit'" /></a-form-item></a-col>
          <a-col :span="12"><a-form-item label="中文动作名称" required><a-input v-model:value="form.nameZh" placeholder="例如：散步" /></a-form-item></a-col>
          <a-col :span="12"><a-form-item label="英文动作名称"><a-input v-model:value="form.nameEn" placeholder="例如：Walk" /></a-form-item></a-col>
          <a-col :span="8"><a-form-item label="生成额度"><a-input-number v-model:value="form.creditCost" :min="0" style="width: 100%" /></a-form-item></a-col>
          <a-col :span="8"><a-form-item label="排序"><a-input-number v-model:value="form.sortOrder" style="width: 100%" /></a-form-item></a-col>
          <a-col :span="8"><a-form-item label="播放 fps"><a-input-number v-model:value="form.fps" :min="1" style="width: 100%" /></a-form-item></a-col>
          <a-col :span="24">
            <a-form-item label="中文气泡对白（每行一条）">
              <a-textarea
                v-model:value="form.messageZh"
                :rows="4"
                placeholder="例如：&#10;看到你啦。&#10;今天也陪你一会儿。&#10;我在这里。"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-form-item label="英文气泡对白（每行一条）">
              <a-textarea
                v-model:value="form.messageEn"
                :rows="4"
                placeholder="For example:&#10;I see you.&#10;I'll stay with you for a while.&#10;I'm right here."
              />
            </a-form-item>
          </a-col>
          <a-col :span="24"><a-form-item label="动作描述 Prompt"><a-textarea v-model:value="form.promptSnippet" :rows="4" /></a-form-item></a-col>
        </a-row>
        <a-space wrap>
          <a-checkbox v-model:checked="form.isDefault">默认生成</a-checkbox>
          <a-checkbox v-model:checked="form.enabled">启用</a-checkbox>
          <a-checkbox v-model:checked="form.mirrored">左右镜像</a-checkbox>
        </a-space>
      </a-form>
    </a-modal>

    <a-drawer title="动作详情" :open="Boolean(detailAction)" width="480" @close="detailAction = null">
      <a-descriptions v-if="detailAction" bordered :column="1" size="small">
        <a-descriptions-item label="Key">{{ detailAction.key }}</a-descriptions-item>
        <a-descriptions-item label="中文名称">{{ detailAction.nameZh || detailAction.name }}</a-descriptions-item>
        <a-descriptions-item label="英文名称">{{ detailAction.nameEn || "未设置" }}</a-descriptions-item>
        <a-descriptions-item label="中文气泡对白">
          <div v-if="actionMessages(detailAction, 'zh').length" class="admin-message-list">
            <span v-for="item in actionMessages(detailAction, 'zh')" :key="item">{{ item }}</span>
          </div>
          <span v-else>未设置</span>
        </a-descriptions-item>
        <a-descriptions-item label="英文气泡对白">
          <div v-if="actionMessages(detailAction, 'en').length" class="admin-message-list">
            <span v-for="item in actionMessages(detailAction, 'en')" :key="item">{{ item }}</span>
          </div>
          <span v-else>未设置</span>
        </a-descriptions-item>
        <a-descriptions-item label="生成成本">{{ detailAction.creditCost }} 次额度</a-descriptions-item>
        <a-descriptions-item label="播放参数">{{ detailAction.fps }} fps · {{ detailAction.mirrored ? "左右镜像" : "单向" }}</a-descriptions-item>
        <a-descriptions-item label="镜像">{{ detailAction.mirrored ? "生成左右镜像" : "单方向动作" }}</a-descriptions-item>
        <a-descriptions-item label="默认生成">{{ detailAction.isDefault ? "是" : "否" }}</a-descriptions-item>
        <a-descriptions-item label="启用状态">{{ detailAction.enabled ? "启用" : "停用" }}</a-descriptions-item>
        <a-descriptions-item label="Prompt">{{ detailAction.promptSnippet || "未设置" }}</a-descriptions-item>
      </a-descriptions>
      <template #footer>
        <a-space v-if="detailAction">
          <a-button type="primary" @click="openEdit(detailAction)">编辑动作</a-button>
          <a-button danger @click="confirmRemove(detailAction)">删除动作</a-button>
        </a-space>
      </template>
    </a-drawer>
  </a-space>
</template>
